import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import isEqual from "lodash.isequal";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";

import { Button } from "@/components/atoms";
import useLocation from "@/lib/hooks/useLocation";

import { CloseIcon } from "@/components/icons/Close";
import { PDPProduct } from "../../page";
import styles from "./coming-soon-modal.module.css";

const ComingSoonModal = ({
  item,
  state,
  product,
  selectedSizeText,
}: {
  item?: { itemId: string; stock: boolean };
  state: [boolean, (newState: boolean) => void];
  product?: PDPProduct;
  selectedSizeText?: string;
}) => {
  const [email, setEmail] = useState("");
  const [modalOpen, setModalOpen] = state;
  const location = useLocation();
  const [locationState, setLocationState] = useState(location.state);
  const [country, setCountry] = useState(location.location);
  const [result, setResult] = useState(false);

  useEffect(() => {
    if (isEqual(location.location, country)) {
      return;
    }
    setCountry(location.location);
    setLocationState(location.state);
  }, [location, country]);

  const notifyMeComingSoon = async (e: FormEvent) => {
    e.preventDefault();
    const apiUrl = process.env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
    const res = await fetch(`${apiUrl}/newsletter-subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        country: country,
        item: item?.itemId,
      }),
    });
    const json = await res.json();
    const success = json.subscribed;
    setResult(success);
  };

  const notifyMeStock = async (e: FormEvent) => {
    e.preventDefault();
    const apiUrl = process.env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
    const res = await fetch(`${apiUrl}/back-in-stock-subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        shipTo: {
          country: country,
          state: locationState,
        },
        item: item?.itemId,
      }),
    });
    const json = await res.json();
    const success = !!json.token;
    setResult(success);
  };

  const notifyMe = product?.comingSoon ? notifyMeComingSoon : notifyMeStock;

  return (
    <Dialog.Root
      open={modalOpen}
      onOpenChange={(e) => {
        setModalOpen(e);
        setResult(false);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="dialog-overlay"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />

        <Dialog.Content
          className={clsx("dialog-content-fade-in", styles.modal)}
          onClick={(e) => e.stopPropagation()}
        >
          <Dialog.Close
            className={styles.close}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <CloseIcon />
          </Dialog.Close>
          {!result ? (
            <>
              <h1 className={clsx(styles.title, styles["text-center"])}>
                {selectedSizeText
                  ? `Size ${selectedSizeText} - ${product?.comingSoon === "1" ? "Coming soon" : "Currently Sold Out"}`
                  : product?.comingSoon === "1"
                    ? "Coming soon"
                    : "The product is out of stock"}
              </h1>
              <p className={clsx(styles.preamble, styles["text-center"])}>
                {product?.comingSoon === "1"
                  ? `Enter your email and we'll let you know as soon as ${product?.name || "this item"} is ready to shop!`
                  : "But don't worry! Sign up with your e-mail here, and we will notify you if the item is back in stock"}
              </p>

              <form onSubmit={notifyMe}>
                <div className={styles["form-group"]}>
                  <input type="hidden" name="_country" value={country} />
                  <input type="hidden" name="_item" value={item?.itemId} />
                  <input type="hidden" name="_state" value={locationState} />
                  <input
                    className={styles.input}
                    type="email"
                    placeholder="Email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className={styles["modal-button-container"]}>
                  <Button
                    color="black"
                    textColor="white"
                    grow
                    type="submit"
                    className={clsx("uppercase", styles.button)}
                  >
                    Notify me
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h1 className={clsx("preamble", styles["text-center"])}>
                Thank you
              </h1>
              <p className={clsx("uppercase h4", styles["text-center"])}>
                We will let you know when it is available
              </p>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ComingSoonModal;
